import { sankey, sankeyLeft, sankeyRight } from 'd3-sankey';
import { cloneDeep } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ISankeyData } from '@src/emp-3.0/types';
import { COLORS } from '@src/emp-3.0/utils/constants';
import { parseNumToPercentage } from '@src/emp-3.0/utils/number';

import {
  CHART_HEIGHT,
  CHART_ZOOM,
  DROP_OFF_DISPLAY_NAME,
  NOT_AVAILABLE_COLOR,
  OTHERS_NODE_DISPLAY_NAME,
} from '../constants';
import { isDropOffNode, isDropOffOrOthersNode, isOtherNode } from '../utils';
import { NodeDropdown } from './NodeDropdown';
import { SankeyLinksComponent } from './SankeyLinks';
import { SankeyNodeLabelsComponent } from './SankeyNodeLabels';
import { SankeyNodesComponent } from './SankeyNodes';
import { SankeyStepsComponent } from './SankeySteps';
import styles from './sankey.less';
import { useSankeyStore } from '../store';
import { doNotZoom } from './utils';

type SankeyProps = {
  data: ISankeyData;
  width?: number;
  height?: number;
  direction?: 'forward' | 'backward';
  selectedNodeIds?: string[];
  onSelectedNodeChange?: (
    selectedNodeIds: string[],
    availableNodes: any[],
    selectedNodeInstances: any[],
  ) => void;
  onFetch?: any;
  mask?: boolean;
};

const parseSankeyNumToPercentage = (number, fixed = 1) => {
  const percentage = parseNumToPercentage(number, fixed);
  if (percentage === '0.0%') {
    return '<0.1%';
  }
  return percentage;
};

const SankeyChart = ({
  data,
  direction,
  width,
  height = CHART_HEIGHT,
  selectedNodeIds,
  onSelectedNodeChange,
  onFetch,
  mask,
}: SankeyProps) => {
  const svgRef = useRef(null);
  const chartRef = useRef(null);
  const [steps, setSteps] = useState<number[]>([]);
  const [hoveredNodeLabel, setHoveredNodeLabel] = useState<any>();
  const [hoveredNode, setHoveredNode] = useState<any>();
  const [hoveredLink, setHoveredLink] = useState<any>();
  // TODO move into link component
  const [nodeTipsPosition, setNodeTipsPosition] = useState<any[]>([0, 0]);
  const [linkLabelPosition, setLinkLabelPosition] = useState<any[]>([0, 0]);
  const [selectedNodesMap, setSelectedNodesMap] = useState({});
  const [availableNodeIds, setAvailableNodeIds] = useState([]);
  const { sankeyRequestBody } = useSankeyStore((state) => state);
  const { top_k_nodes } = sankeyRequestBody;
  const isLTR = direction === 'forward';
  const zoom = doNotZoom() ? 1 : CHART_ZOOM;
  const chartWidth = useMemo(() => {
    const width = steps.length * 400;
    if (!isLTR) return width - 300;
    return width;
  }, [steps, isLTR]);

  const sankeyGenerator = useMemo(() => {
    return sankey() // Main function of the d3-sankey plugin that computes the layout
      .nodeWidth(45) // width of the node in pixels
      .nodePadding(80) // space between nodes
      .extent([
        // chart area:
        [10, 10], // top-left coordinates
        [chartWidth - 10, CHART_HEIGHT - 10], // bottom-right coordinates
      ])
      .nodeId((node) => {
        return node.id;
      })
      .nodeSort((prev, next) => {
        // must return a value less than 0 if the first node should be above the second,
        // and a value greater than 0 if the second node should be above the first,
        // or 0 if the order is not specified
        const { display_name: prevName, value: prevValue } = prev;
        const { display_name: nextName, value: nextValue } = next;

        if (nextName === DROP_OFF_DISPLAY_NAME && prevName === OTHERS_NODE_DISPLAY_NAME) return -1;
        if (prevName === DROP_OFF_DISPLAY_NAME && nextName === OTHERS_NODE_DISPLAY_NAME) return 1;

        if (prevName === DROP_OFF_DISPLAY_NAME || prevName === OTHERS_NODE_DISPLAY_NAME) return 1;
        if (nextName === DROP_OFF_DISPLAY_NAME || nextName === OTHERS_NODE_DISPLAY_NAME) return -1;

        return nextValue - prevValue;
      })
      .nodeAlign(isLTR ? sankeyLeft : sankeyRight); // Accessor function: how to retrieve the id that defines each node. This id is then used for the source and target props of links
  }, [sankey, sankeyLeft, steps, isLTR]);

  const { nodes, links } = useMemo(() => {
    if (data?.links?.length && data?.nodes?.length) {
      const copiedData = cloneDeep(data);
      return sankeyGenerator(copiedData);
    }
    return { nodes: [], links: [] };
  }, [sankeyGenerator, data]);

  useMemo(() => {
    const selectedNodesMap = {};
    const selectedNodeInstances = nodes.filter((n) => selectedNodeIds?.includes(n.id));

    selectedNodeInstances.forEach((n) => {
      selectedNodesMap[n.level] = n;
    });
    setSelectedNodesMap(selectedNodesMap);
    // do not change the dependence
  }, [nodes, selectedNodeIds?.length]);

  // Setup color for nodes
  useEffect(() => {
    nodes.forEach((node, index) => {
      if (isDropOffNode(node)) {
        node.color = NOT_AVAILABLE_COLOR;
      } else {
        node.color = COLORS[index % COLORS.length];
      }
    });
  }, [nodes]);

  useEffect(() => {
    const stepsSet = new Set<number>(data.nodes.map((n) => n.level));
    const steps = [...stepsSet];
    setSteps(steps);
  }, [data]);

  // check node is available
  const isAvailableNode = (node) => {
    if (availableNodeIds.length === 0) return true;
    return availableNodeIds.includes(node.id);
  };

  // check link is available
  const isAvailableLink = (link) => {
    if (availableNodeIds.length === 0) return true;
    const { source, target } = link;
    return availableNodeIds.includes(source.id) && availableNodeIds.includes(target.id);
  };

  const findFullPaths = (node, currentPath = [], res = []): any[][] => {
    if (!node) {
      return [];
    }
    const selectedNodeInSameLayer = selectedNodesMap?.[node.level];
    if (
      !selectedNodeInSameLayer ||
      (selectedNodeInSameLayer && selectedNodeInSameLayer.id === node.id)
    ) {
      currentPath.push(node);
      if (isLTR) {
        if (node.targetLinks.length !== 0) {
          // source for prev
          node.targetLinks.forEach((link) => {
            const prev = link.source;
            findFullPaths(prev, [...currentPath], res);
          });
        } else {
          res.push(currentPath.slice());
        }
      } else {
        if (node.sourceLinks.length !== 0) {
          // source for prev
          node.sourceLinks.forEach((link) => {
            const prev = link.target;
            findFullPaths(prev, [...currentPath], res);
          });
        } else {
          res.push(currentPath.slice());
        }
      }
    }
    return res;
  };

  /*
   *
   * Nodes
   *
   * */

  const findAvailableNodeIds = (rootNode) => {
    if (!rootNode) return [];
    if (rootNode.id === nodes[0].id) return nodes.map((n) => n.id);
    const availableNodeIds = [];
    let nextLink = 'sourceLinks';
    let nextNode = 'target';

    if (!isLTR) {
      nextLink = 'targetLinks';
      nextNode = 'source';
    }

    function traverse(node) {
      availableNodeIds.push(node.id);
      if (node[nextLink].length !== 0) {
        node[nextLink].forEach((link) => {
          const next = link[nextNode];
          traverse(next);
        });
      }
    }

    try {
      traverse(rootNode);
    } catch (error) {
      console.log('Get available node error:', error);
      return nodes.map((n) => n.id);
    }
    return availableNodeIds;
  };

  const nodeTips = useMemo(() => {
    if (!hoveredNode || !isAvailableNode(hoveredNode)) return;
    // find the available paths
    const availableLinks = links
      .filter((link) => {
        return link.target.id === hoveredNode.id && isAvailableLink(link);
      })
      .sort((next, current) => current.value - next.value);
    if (!availableLinks.length) return;

    return availableLinks.map((i) => {
      const percentage = parseSankeyNumToPercentage(i.value / hoveredNode.value);
      return (
        <div key={i.source.id + i.target.id}>
          {percentage} from {i.source.display_name}
        </div>
      );
    });
  }, [hoveredNode]);

  const nodeTipsStyle = useMemo(() => {
    return {
      position: 'fixed',
      top: nodeTipsPosition[1] / zoom + 10 + 'px',
      left: nodeTipsPosition[0] / zoom + 10 + 'px',
      display: hoveredNode && nodeTips ? 'block' : 'none',
    };
  }, [nodeTipsPosition, hoveredNode, nodeTips]);

  const onMouseEnterNodeLabel = (node) => {
    setHoveredNodeLabel(node);
  };

  const onMouseLeaveNodeLabel = () => {
    setHoveredNodeLabel(null);
  };

  const onClickNode = (node) => {
    if (!isAvailableNode(node) || isDropOffNode(node)) return;

    const { level } = node;

    if (selectedNodesMap[level]) {
      delete selectedNodesMap[level];
    } else {
      selectedNodesMap[level] = node;
    }
    setSelectedNodesMap({ ...selectedNodesMap });
  };

  const getAvailableNodesBySelectedNodes = () => {
    // DFS
    let startPoint = Math.max.apply(null, Object.keys(selectedNodesMap) || 0);
    if (!isLTR) {
      startPoint = Math.min.apply(null, Object.keys(selectedNodesMap) || 0);
    }
    const fullPaths = findFullPaths(selectedNodesMap[startPoint], [], []);

    // get all the available node ids
    const backwardAvailableNodeIds = findAvailableNodeIds(selectedNodesMap[startPoint]);
    const availableNodeIdSet = new Set([...backwardAvailableNodeIds]);
    fullPaths.forEach((path) => {
      path.forEach((node) => {
        availableNodeIdSet.add(node.id);
      });
    });
    return [...Array.from(availableNodeIdSet)];
  };

  useEffect(() => {
    const selectedNodeIds = Object.values(selectedNodesMap).map((n) => n.id);
    const availableNodeIds = getAvailableNodesBySelectedNodes();
    const availableNodes = nodes.filter((n) => availableNodeIds.includes(n.id));
    const selectedNodeInstances = [...nodes.filter((n) => selectedNodeIds?.includes(n.id))];

    setAvailableNodeIds(availableNodeIds);

    let mergedSelectedNodeIds = selectedNodeIds;

    // When you select any node you must ensure that the initial node is selected.
    if (selectedNodeIds.length && !selectedNodeIds.includes(nodes[0]?.id)) {
      mergedSelectedNodeIds = [nodes[0]?.id, ...selectedNodeIds];
    }

    onSelectedNodeChange?.(mergedSelectedNodeIds, availableNodes, selectedNodeInstances);
    // do not change the dependence
  }, [selectedNodesMap]);

  /*
   *
   * Links
   *
   * */

  const linkLabelStyle = useMemo(() => {
    return {
      position: 'fixed',
      top: linkLabelPosition[1] / zoom + 10 + 'px',
      left: linkLabelPosition[0] / zoom + 10 + 'px',
      display: hoveredLink ? 'block' : 'none',
    };
  }, [linkLabelPosition, hoveredLink]);

  const linkLabelText = useMemo(() => {
    if (!hoveredLink) return;

    const { source, target } = hoveredLink;
    const percent = parseSankeyNumToPercentage(hoveredLink.value / source?.value);
    return `${percent} of ${source.display_name} -> ${target.display_name}`;
  }, [hoveredLink]);

  const getLinkColor = (link) => {
    if (!isAvailableLink(link)) return NOT_AVAILABLE_COLOR;

    return link.source.color;
  };

  const labelStyle = useMemo(() => {
    const baseStyle = {
      display: 'flex',
      flexDirection: 'column',
    };
    if (!isLTR) {
      return {
        ...baseStyle,
        textAlign: 'right',
        alignItems: 'flex-end',
      };
    }
    return {
      ...baseStyle,
      textAlign: 'left',
      alignItems: 'flex-start',
    };
  }, [isLTR]);

  const getNodeCursorStyle = (node, mask) => {
    if (isAvailableNode(node)) {
      if (isDropOffNode(node) || mask) {
        return { cursor: 'not-allowed' };
      }
      return { cursor: 'pointer' };
    } else {
      return { cursor: 'default' };
    }
  };

  return (
    <div style={{ overflow: 'scroll' }}>
      <div
        style={{
          position: 'relative',
          zoom: zoom,
          left: isLTR ? 0 : 300,
          height: CHART_HEIGHT + 80,
          width: chartWidth,
        }}
        ref={chartRef}
      >
        <SankeyStepsComponent
          nodes={nodes}
          steps={steps}
          render={(step) => {
            return (
              <div className={styles.stepLabel}>
                <div>Step {step + 1}</div>
              </div>
            );
          }}
        />
        <svg ref={svgRef} width={chartWidth} height={CHART_HEIGHT} style={{ marginTop: '70px' }}>
          <SankeyNodesComponent
            nodes={nodes}
            colorFunc={(node) => {
              if (isAvailableNode(node)) {
                return node.color;
              }
              return NOT_AVAILABLE_COLOR;
            }}
            onMouseEnter={(_, node) => {
              setHoveredNode(node);
            }}
            onMouseMove={(e) => {
              setNodeTipsPosition([e.clientX, e.clientY]);
            }}
            onMouseOut={() => {
              setHoveredNode(null);
            }}
          />
          <SankeyLinksComponent
            links={links}
            colorFunc={getLinkColor}
            onMouseEnter={(_, link) => {
              setHoveredLink(link);
            }}
            onMouseMove={(e) => {
              setLinkLabelPosition([e.clientX, e.clientY]);
            }}
            onMouseOut={() => {
              setHoveredLink(null);
            }}
          />
        </svg>
        <div className={styles.linkLabel} style={linkLabelStyle}>
          {linkLabelText}
        </div>

        <div className={styles.linkLabel} style={nodeTipsStyle}>
          {nodeTips}
        </div>
        <SankeyNodeLabelsComponent
          nodes={nodes}
          width={340}
          isLTR={isLTR}
          textFunc={(node) => {
            const isHovered = hoveredNodeLabel?.display_name === node.display_name;
            const isActive = selectedNodesMap?.[node.level]?.id === node.id;
            const cursorStyle = getNodeCursorStyle(node, mask);

            return (
              <div
                className={styles.nodeLabel}
                onClick={() => !mask && onClickNode(node)}
                style={{ ...labelStyle, ...cursorStyle }}
              >
                <div style={{ display: 'flex' }}>
                  {isLTR && (
                    <div className={styles.nodePercentageContainer}>
                      <div className={styles.nodePercentage}>
                        {parseSankeyNumToPercentage(node.percentage_by_level)}
                      </div>
                      <div className={styles.nodePercentageLabel}>
                        {`${parseSankeyNumToPercentage(node.percentage_by_level)} of step ${
                          node.level + 1
                        }`}
                      </div>
                    </div>
                  )}
                  <div style={labelStyle}>
                    <div
                      className={`
                    ${styles.nodeLabelText}
                    ${isHovered ? styles.nodeLabelTextHovered : ''}
                    ${isActive ? styles.nodeLabelTextActive : ''}
                  `}
                      onMouseEnter={() => onMouseEnterNodeLabel(node)}
                      onMouseLeave={() => onMouseLeaveNodeLabel()}
                      style={!isLTR ? { textAlign: 'right' } : {}}
                    >
                      <div className={styles.nodeDisplayNameBox}>
                        <div className={styles.nodeDisplayName}>{node.display_name}</div>
                        <div className={styles.nodeKebab}>
                          {!isDropOffOrOthersNode(node) && !mask && (
                            <NodeDropdown
                              node={node}
                              chartRef={chartRef}
                              onRefreshSankey={onFetch}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                    {/*HP-6565 hide tooltips for other nodes*/}
                    {isHovered && (
                      <div className={styles.nodeLabelCoverage}>
                        {isDropOffNode(node)
                          ? 'Out of events selected in the left panel'
                          : isOtherNode(node)
                          ? `Nodes outside the top ${top_k_nodes} are folded into Other`
                          : node.display_name}
                        {/*{`${parseSankeyNumToPercentage(node.percentage_of_total)} paths contain “${*/}
                      </div>
                    )}
                  </div>
                  {!isLTR && (
                    <div className={styles.nodePercentageContainer}>
                      <div className={styles.nodePercentage}>
                        {parseSankeyNumToPercentage(node.percentage_by_level)}
                      </div>
                      <div className={styles.nodePercentageLabel}>
                        {`${parseSankeyNumToPercentage(node.percentage_by_level)} of step ${
                          node.level + 1
                        }`}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

export default SankeyChart;
